// Copyright 2026 Hardfin, Inc.

/**
 * Minimal address shape used by location utilities.
 * Only requires the formattedAddress field that the utilities access.
 */
interface LocationAddress {
  formattedAddress: string | null;
}

/**
 * Minimal location shape used by location utilities.
 * Accepts any location-like object with the fields actually used by the
 * utility functions. This is intentionally loose so that both full
 * SparseLocationWithParent objects and simplified interfaces (like
 * LocationForLabel) can be passed without casting.
 */
interface LocationWithParentFields {
  name: string;
  type?: 'SITE' | 'ZONE';
  zoneCount?: number;
  address?: LocationAddress | null;
  isThirdPartyEnabled?: boolean;
  parentLocation?: {
    name: string;
    address?: LocationAddress | null;
    isThirdPartyEnabled?: boolean;
  } | null;
}

/**
 * Returns the effective 3PL enabled status for a location.
 * Zones inherit 3PL from their parent site.
 */
export function isEffectiveThirdPartyEnabled(
  location: Pick<
    LocationWithParentFields,
    'type' | 'isThirdPartyEnabled' | 'parentLocation'
  > | null
): boolean {
  if (location == null) return false;
  if (location.isThirdPartyEnabled) return true;
  return (
    location.type === 'ZONE' &&
    location.parentLocation?.isThirdPartyEnabled === true
  );
}

/**
 * Returns the display name for a location.
 * Zones render as "Parent — Zone".
 * Sites with child zones render as "Site — Default".
 */
export function getEffectiveLocationName(
  location: Pick<
    LocationWithParentFields,
    'name' | 'type' | 'parentLocation' | 'zoneCount'
  >
): string {
  if (location.type === 'ZONE' && location.parentLocation != null) {
    return `${location.parentLocation.name} \u2014 ${location.name}`;
  }
  if (location.type === 'SITE' && (location.zoneCount ?? 0) > 0) {
    return `${location.name} \u2014 Default`;
  }
  return location.name;
}

/**
 * Returns the effective address for a location.
 * Zones inherit the parent site's address.
 */
export function getEffectiveAddress(
  location: Pick<
    LocationWithParentFields,
    'type' | 'address' | 'parentLocation'
  >
): string | null {
  if (location.type === 'ZONE' && location.parentLocation != null) {
    return location.parentLocation.address?.formattedAddress ?? null;
  }
  return location.address?.formattedAddress ?? null;
}
